@if (!file) {
  <div
    class="drop-zone flex flex-col items-center justify-center rounded-md border-2 border-dashed bg-gray-100 p-6 text-gray-600 transition duration-150 ease-in-out"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave($event)"
    (drop)="onDrop($event)"
    [class.drag-active]="isDragging"
    tabindex="0"
    role="region"
    [attr.aria-label]="
      'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.ARIA_LABEL' | translate
    "
    aria-describedby="drop-zone-description"
  >
    <mat-icon>cloud_upload</mat-icon>
    <p id="drop-zone-description" class="cdk-visually-hidden">
      {{
        'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.ACCESSIBLE_INSTRUCTIONS'
          | translate
      }}
    </p>
    <p>{{ 'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.DESCRIPTION' | translate }}</p>
    <p>{{ 'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.OR' | translate }}</p>
    <button mat-raised-button color="primary" (click)="fileInput.click()">
      {{ 'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.SELECT_CSV' | translate }}
    </button>
    <input
      #fileInput
      type="file"
      class="hidden"
      (change)="onFileSelected($event)"
      accept=".csv"
    />
  </div>
} @else if (!displayColumnMatch()) {
  <mat-card-header class="flex items-center">
    <button
      [attr.aria-label]="
        'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.PREVIEW.BACK_BUTTON_ARIA_LABEL'
          | translate
      "
      (click)="removeFile()"
      class="absolute left-0 ml-4"
    >
      <mat-icon>arrow_back</mat-icon>
    </button>
    <mat-card-title class="ml-8">
      {{ file.name }}
    </mat-card-title>
  </mat-card-header>
  <mat-card-content class="code-block">
    <h2 class="mt-4 text-lg">
      {{
        'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.PREVIEW.FILE_CONTENT' | translate
      }}
    </h2>
    <pre class="overflow-x-auto rounded bg-gray-100 p-4 font-mono">{{
      fileContent
    }}</pre>
  </mat-card-content>

  @if (csvData.length > 0) {
    <mat-divider />
    <mat-card-content class="code-block">
      <h2 class="mt-4 text-lg">
        {{
          'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.PREVIEW.IMPORT_PREVIEW_TITLE'
            | translate
        }}
      </h2>
      <mat-checkbox
        class="mb-2"
        [checked]="hasHeader()"
        (change)="onHasHeaderChange($event)"
        [disabled]="csvData.length === 1"
      >
        {{
          'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.PREVIEW.HAS_HEADERS' | translate
        }}
      </mat-checkbox>
      <div class="overflow-x-auto">
        <mat-table [dataSource]="dataSource" class="h-50">
          <caption class="cdk-visually-hidden">
            {{
              'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.PREVIEW.TABLE_CAPTION'
                | translate
            }}
          </caption>
          @if (displayedColumns.length > 0) {
            @for (col of displayedColumns; track $index) {
              <ng-container matColumnDef="{{ col.value }}">
                <th
                  mat-header-cell
                  *matHeaderCellDef
                  class="border-r whitespace-nowrap"
                  [class.text-gray-500]="hasHeader()"
                >
                  {{ col.value }}
                </th>
                <td
                  mat-cell
                  *matCellDef="let row"
                  class="border-r border-gray-300 whitespace-nowrap"
                >
                  {{ row[$index] }}
                </td>
              </ng-container>
            }
            @if (hasHeader()) {
              <tr
                mat-header-row
                *matHeaderRowDef="getDisplayedColumnsValues()"
              ></tr>
            }
            <tr
              mat-row
              *matRowDef="let row; columns: getDisplayedColumnsValues()"
            ></tr>
          } @else {
            <div>
              <p>
                {{
                  'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.PREVIEW.NO_DATA'
                    | translate
                }}
              </p>
            </div>
          }
        </mat-table>
      </div>
      <mat-card-actions class="flex justify-end space-x-2 p-4">
        @if (isCsvDataEmpty()) {
          <p class="p-2 text-red-600">
            {{
              'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.PREVIEW.EMPTY_CSV'
                | translate
            }}
          </p>
          <button mat-raised-button (click)="removeFile()">
            {{
              'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.PREVIEW.BACK_BUTTON'
                | translate
            }}
          </button>
        }
        <button
          mat-raised-button
          (click)="pairColumns()"
          [disabled]="isCsvDataEmpty()"
        >
          {{
            'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.PREVIEW.NEXT_BUTTON'
              | translate
          }}
        </button>
      </mat-card-actions>
    </mat-card-content>
  }
} @else if (!isDataImportCompleted()) {
  <div cdkDropListGroup class="space-y-8">
    <div
      aria-live="polite"
      class="cdk-visually-hidden"
      id="drag-drop-announcer"
    ></div>
    <mat-card-header class="flex items-center">
      <button
        [attr.aria-label]="
          'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.COLUMN_MATCHING.BACK_BUTTON_ARIA_LABEL'
            | translate
        "
        (click)="back()"
        class="absolute left-0 ml-4"
      >
        <mat-icon>arrow_back</mat-icon>
      </button>
      <mat-card-title class="ml-8">
        {{ file.name }}
      </mat-card-title>
    </mat-card-header>
    <div>
      <h2 class="text-lg" id="csv-columns-heading">
        {{
          'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.COLUMN_MATCHING.CSV_DATA'
            | translate
        }}
      </h2>
      <p id="csv-columns-instructions" class="cdk-visually-hidden">
        {{
          'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.COLUMN_MATCHING.INSTRUCTIONS'
            | translate
        }}
      </p>

      <div
        cdkDropList
        [cdkDropListData]="displayedColumns"
        class="grid max-h-50 auto-cols-auto grid-flow-col gap-4 overflow-x-auto"
        cdkDropListSortingDisabled
        role="listbox"
        aria-labelledby="csv-columns-heading csv-columns-instructions"
      >
        @for (item of displayedColumns; track item; let columnIndex = $index) {
          <div
            cdkDrag
            [cdkDragData]="item"
            cdkDragPreview
            [cdkDragPreviewClass]="[
              'rounded',
              'border',
              '!bg-blue-100',
              'border-gray-300',
            ]"
            [cdkDragDisabled]="item.disabled"
            [class.disabled]="item.disabled"
            tabindex="0"
            role="option"
            aria-describedby="csv-columns-instructions"
            class="relative w-50 cursor-grab rounded border border-gray-300 bg-blue-100 p-4 {{
              item.disabled
                ? 'opacity-70 cursor-not-allowed bg-gray-200 border-gray-400 text-gray-400'
                : ''
            }}"
          >
            {{ item.value }}
            @for (i of dataSourceIndices(); track i) {
              <p
                class="overflow-hidden text-ellipsis whitespace-nowrap text-gray-400"
              >
                {{ dataSource.data[i][columnIndex] }}
              </p>
            }
            @if (item.disabled) {
              <button
                class="absolute top-0 right-0 hover:text-green-700"
                (click)="returnToDefault(item)"
                [attr.aria-label]="
                  'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.COLUMN_MATCHING.RESET_BUTTON_ARIA_LABEL'
                    | translate: { column: item.value }
                "
              >
                <mat-icon>replay</mat-icon>
              </button>
            }
          </div>
        }
      </div>
    </div>

    <div>
      <h2 class="text-lg">
        {{
          'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.PREVIEW.IMPORT_PREVIEW_TITLE'
            | translate
        }}
      </h2>
      <p id="import-columns-instructions" class="cdk-visually-hidden">
        {{
          'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.COLUMN_MATCHING.IMPORT_COLUMNS_INSTRUCTIONS'
            | translate
        }}
      </p>
      <div
        class="grid max-h-50 auto-cols-auto grid-flow-col gap-4 overflow-x-auto overflow-y-hidden"
        role="list"
        [attr.aria-labelledby]="
          'import-columns-heading import-columns-instructions'
        "
      >
        @for (
          importingColumn of importingColumns;
          track importingColumn;
          let columnIndex = $index
        ) {
          <div
            cdkDropList
            class="relative h-40 w-50 items-center rounded-md border-2 border-dashed bg-gray-100 text-gray-600 transition duration-150 ease-in-out"
            [cdkDropListData]="importingColumns"
            (cdkDropListDropped)="dropToImport($event, columnIndex)"
            cdkDropListOrientation="horizontal"
            [class.drag-active]="isDragging"
            tabindex="0"
            role="listitem"
            [attr.aria-label]="importingColumn.value"
            aria-describedby="import-columns-instructions"
            [attr.aria-dropeffect]="isDragging ? 'move' : 'none'"
          >
            <div
              class="h-full p-4
                 {{
                importingColumn.disabled
                  ? 'opacity-70 cursor-not-allowed  border-gray-400'
                  : 'rounded border bg-blue-100 border-gray-300 text-black'
              }}"
            >
              {{ importingColumn.value }}
              @if (!importingColumn.disabled) {
                @for (i of dataSourceIndices(); track i) {
                  <p
                    class="overflow-hidden text-ellipsis whitespace-nowrap text-gray-400"
                  >
                    {{ dataSource.data[i][columnIndex] }}
                  </p>
                }
              }
            </div>
            <button
              class="absolute top-0 right-0 hover:text-red-700"
              (click)="returnToDisplayed(importingColumn, columnIndex)"
              [attr.aria-label]="
                'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.COLUMN_MATCHING.REMOVE_BUTTON_ARIA_LABEL'
                  | translate: { column: importingColumn.value }
              "
            >
              <mat-icon>close</mat-icon>
            </button>
          </div>
        }
      </div>
    </div>
    <div class="flex items-center justify-end gap-6">
      <button
        mat-raised-button
        (click)="onImportButtonClicked()"
        [disabled]="isPairValid()"
        class="flex flex-row items-center justify-center"
      >
        {{
          'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.COLUMN_MATCHING.IMPORT_BUTTON'
            | translate
        }}
      </button>
      @if (isImportingData()) {
        <div class="flex w-full flex-col items-center gap-2">
          <p class="mb-0">
            {{
              'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.COLUMN_MATCHING.LOADING_MESSAGE'
                | translate
            }}
          </p>
          <mat-progress-bar
            mode="indeterminate"
            [attr.aria-label]="
              'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.COLUMN_MATCHING.PROGRESS_BAR_ARIA_LABEL'
                | translate
            "
          />
        </div>
      }
    </div>
  </div>
} @else if (!isDataImportValid()) {
  <div class="flex w-full flex-col items-center gap-2 space-y-4">
    <p class="text-red-500">
      {{
        'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.COMPLETION.COMPLETION_MESSAGE_FAILED'
          | translate
      }}
    </p>
    <button
      mat-raised-button
      (click)="toggle()"
      class="flex flex-row items-center justify-center"
    >
      {{
        'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.COMPLETION.COMPLETION_BUTTON'
          | translate
      }}
    </button>
  </div>
} @else {
  <div class="flex w-full flex-col items-center gap-2 space-y-4">
    <p class="text-green-500">
      {{
        'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.COMPLETION.COMPLETION_MESSAGE_SUCCESS'
          | translate
      }}
    </p>
    <button
      mat-raised-button
      (click)="toggle()"
      class="flex flex-row items-center justify-center"
    >
      {{
        'ADMIN_PAGE.USERS.TABLE.DRAG_AND_DROP.COMPLETION.COMPLETION_BUTTON'
          | translate
      }}
    </button>
  </div>
}
